import React, { useEffect, useState, useCallback, useRef } from "react";
import styles from "./searchLocation.module.scss";
import { useQuery } from "@tanstack/react-query";
import { getImageUrlWithMaxWidth } from "../../../utils";
import { ButtonBox } from "../../button/Button";
import Blank from "../../empty/blank/Blank";
import InputBox from "../../form/inputBox/InputBox";
import DataSection from "../../form/dataSection/DataSection";
import { SkeletonBase } from "../../addons/skeleton/Skeleton";

const SearchLocation = ({
  keyword,
  setKeyword,
  onCompleted,
  hideModal,
  queryKey,
  title,
  placeholder,
  isAllowCustomValue = false,
  noResultText,
  noResultButton,
  fetchSearchData,
  queryClient,
}: {
  keyword: string;
  setKeyword: (value: string) => void;
  onCompleted?: (value: any) => void;
  hideModal?: () => void;
  queryKey: string;
  title?: string | React.ReactNode;
  placeholder?: string;
  isAllowCustomValue?: boolean;
  noResultText?: string | React.ReactNode;
  noResultButton?: {
    text: string | React.ReactNode;
    onClick?: () => void;
  };
  fetchSearchData: any;
  queryClient?: any;
}) => {
  const [searchKeyword, setSearchKeyword] = useState(keyword || "");
  const searchResultRef = useRef<null | boolean>(null);

  const { data, isLoading } = useQuery(
    {
      queryKey: [queryKey || "searchLocation", { name: searchKeyword }],
      queryFn: () => fetchSearchData(),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 1000 * 60 * 5, // 5분 동안 갱신하지 않음
    },
    queryClient,
  );
  const [searchedList, setSearchedList] = useState<any[]>([]);

  useEffect(() => {
    if (data) {
      setSearchedList(data);
      searchResultRef.current = data?.length > 0;
    }
  }, [data]);

  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    if (searchKeyword.trim().length === 0) {
      searchResultRef.current = null;
      return;
    }
    setTimeout(() => {
      setSearchLoading(searchResultRef.current === null && isLoading ? true : false);
      searchResultRef.current = null;
    }, 200);
  }, [isLoading, searchKeyword]);

  // 완료 처리 핸들러
  const handleCompleted = (value: Partial<any>) => {
    onCompleted && onCompleted(value);
    hideModal && hideModal();
  };

  const skeletonItems = Array(9).fill(null); // 스켈레톤 아이템
  const inputRef = useRef<HTMLInputElement | null>(null);

  const typingTimeout = useRef<NodeJS.Timeout | null>(null);
  // 값 변경 핸들러 (검색어 입력))
  const handleChangeEvent = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setKeyword(value);
    if (value === "") {
      setSearchKeyword("");
    }
  }, []);

  useEffect(() => {
    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current);
    }
    typingTimeout.current = setTimeout(() => {
      const value = inputRef.current ? inputRef.current.value : "";
      let currentKeyword = value.trim();
      setSearchKeyword(currentKeyword);
    }, 500);
  }, [keyword]);

  useEffect(() => {
    if (searchKeyword.trim().length === 0) {
      setSearchedList([]);
    }
  }, [searchKeyword]);

  return (
    <div className={styles["search-contents"]}>
      <DataSection title={title} titleSize="giant">
        <>
          <Blank height={13} />
          <InputBox
            type="search"
            onChange={handleChangeEvent}
            placeholder={placeholder}
            value={keyword}
            ref={inputRef}
          />
        </>
      </DataSection>
      <div className={styles["searched-list-wrap"]}>
        <ul>
          {searchLoading && isLoading && (
            <>
              {skeletonItems.map((_, index) => (
                <li key={index} className={styles["list-skeleton"]}>
                  <div className={`${styles["thumb"]} ${styles["thumb-skeleton"]}`}>
                    <SkeletonBase width="100%" height="100%" position="absolute" />
                  </div>
                  <div className={styles["details"]}>
                    <span
                      className={styles["name"]}
                      style={{ display: "block", width: "8em", height: "21px" }}
                    >
                      <SkeletonBase width="100%" height="100%" position="absolute" />
                    </span>
                    <span
                      className={styles["region"]}
                      style={{
                        display: "block",
                        width: "15em",
                        height: "21px",
                      }}
                    >
                      <SkeletonBase width="100%" height="100%" position="absolute" />
                    </span>
                  </div>
                </li>
              ))}
            </>
          )}
          {searchedList &&
            searchedList.map((location, index) => (
              <li onClick={() => handleCompleted(location)} key={index}>
                {location.coverUrl && (
                  <div className={styles["thumb"]}>
                    <img
                      src={
                        location?.coverUrl
                          ? getImageUrlWithMaxWidth(location.coverUrl, 150)
                          : location?.logoUrl
                            ? getImageUrlWithMaxWidth(location.logoUrl, 150)
                            : ""
                      }
                      onError={(e) => {
                        if (e.target instanceof HTMLImageElement) {
                          e.target.parentElement?.classList.add("error");
                        }
                      }}
                      onLoad={(e) => {
                        if (e.target instanceof HTMLImageElement) {
                          e.target.parentElement?.classList.add("loaded");
                        }
                      }}
                      loading="lazy"
                      alt={location.name}
                    />
                  </div>
                )}
                <div className={styles["details"]}>
                  <span className={styles["name"]}>{location.name}</span>
                  <span className={styles["region"]}>{location.region}</span>
                </div>
              </li>
            ))}
          {isAllowCustomValue && keyword.trim().length > 0 && (
            <li onClick={() => handleCompleted({ name: keyword.trim() })} key={"custom"}>
              <div className={styles["details"]}>
                <span className={styles["name"]}>{keyword.trim()}</span>
              </div>
            </li>
          )}
          {!isAllowCustomValue && <Blank height={39} />}
        </ul>
        {!searchLoading &&
          !isLoading &&
          searchKeyword.trim().length > 0 &&
          keyword.trim().length > 0 &&
          searchKeyword === keyword.trim() &&
          searchedList &&
          searchedList.length === 0 && (
            <div className={styles["custom-check-wrap"]}>
              <Blank height={16} />
              <p>{`“${keyword.trim()}”${noResultText}`}</p>
              <Blank height={18} />
              {noResultButton && (
                <>
                  <ButtonBox
                    size="medium"
                    theme="brown0"
                    autoWidth
                    onClick={() => {
                      if (isAllowCustomValue) {
                        handleCompleted({
                          name: keyword.trim(),
                        });
                      }
                      noResultButton?.onClick && noResultButton.onClick();
                    }}
                  >
                    {noResultButton.text}
                  </ButtonBox>
                  <Blank height={8} />
                </>
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default SearchLocation;
